import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { SpacedMultiplicationSign, TooltippedIcon } from '@mews-commander/ui';
import { IconName, Stack } from '@optimus-web/core';
import { ApplicationState, CommanderThunkDispatch, isHoldingState } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import { Reservation } from '@mews-commander/data-access';
import { AppClockServiceProps, withAppClockService } from '@mews/framework';
import {
    getCurrentEnterpriseReservation,
    getCurrentEnterpriseReservationCustomerId,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { hasCustomerValidExternalCreditCard } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { editProfile } from '../../actions/profile';
import { openBillingAccount, openPayments, openTimeLine } from '../../actions';

interface TimelineActionArguments {
    reservationId: string;
    serviceId: string;
    openInNewTab: boolean;
}

interface OwnProps {
    reservationId: string;
    refetchGroup: () => void;
}
interface StateProps {
    reservation: Reservation | undefined;
    customerHasValidExternalCreditCard: boolean;
}
interface DispatchProps {
    editProfileScreen: (customerId: string) => void;
    openBillingScreen: (customerId: string) => void;
    openPaymentsScreen: (customerId: string) => void;
    openTimeline: (timelineArguments: TimelineActionArguments) => void;
}
type Props = OwnProps & StateProps & DispatchProps;

const ReservationNavigationButtons: React.FC<Props> = ({
    reservation,
    editProfileScreen,
    openBillingScreen,
    openPaymentsScreen,
    openTimeline,
    customerHasValidExternalCreditCard,
}) => {
    if (!reservation) {
        return null;
    }
    const { adultCount, childCount, customerId, id, serviceId, serviceOrderState } = reservation;
    const handleEditProfile = () => customerId && editProfileScreen(customerId);
    const handleBillingScreen = () => customerId && openBillingScreen(customerId);
    const handlePaymentsScreen = () => customerId && openPaymentsScreen(customerId);
    const handleTimeline = (event: React.MouseEvent<HTMLElement>) =>
        openTimeline({ reservationId: id, serviceId, openInNewTab: event.metaKey || event.ctrlKey });
    return (
        <Stack
            tabletMax={{
                vertical: true,
            }}
            spacing="100"
        >
            <span>
                {(adultCount ?? 0) + (childCount ?? 0)}
                {SpacedMultiplicationSign}
            </span>
            <TooltippedIcon value={IconName.Profile} tooltip={TextKey.EditProfile} onClick={handleEditProfile} />
            <TooltippedIcon value={IconName.PaymentReport} tooltip={TextKey.Billing} onClick={handleBillingScreen} />
            {isHoldingState(serviceOrderState) ? (
                <TooltippedIcon value={IconName.Timeline} tooltip={TextKey.TimeLine} onClick={handleTimeline} />
            ) : null}
            {customerHasValidExternalCreditCard ? (
                <TooltippedIcon
                    value={IconName.PaymentCard}
                    tooltip={TextKey.PaymentCard}
                    onClick={handlePaymentsScreen}
                />
            ) : null}
        </Stack>
    );
};

type OwnPropsWithAppClockService = OwnProps & AppClockServiceProps;

export default pipe(
    ReservationNavigationButtons,
    connect<StateProps, DispatchProps, OwnPropsWithAppClockService, ApplicationState>(
        createStructuredSelector({
            reservation: (state: ApplicationState, { reservationId }: OwnPropsWithAppClockService) =>
                getCurrentEnterpriseReservation(state, { id: reservationId }),
            customerHasValidExternalCreditCard: (
                state: ApplicationState,
                { reservationId, appClockService }: OwnPropsWithAppClockService
            ) => {
                const accountId = getCurrentEnterpriseReservationCustomerId(state, { id: reservationId });
                return accountId ? hasCustomerValidExternalCreditCard(state, { accountId, appClockService }) : false;
            },
        }),
        (dispatch: CommanderThunkDispatch) => ({
            editProfileScreen: (accountId) => dispatch(editProfile({ accountId })),
            openBillingScreen: (accountId) => dispatch(openBillingAccount({ accountId })),
            openPaymentsScreen: (accountId) => dispatch(openPayments({ accountId })),
            openTimeline: (timelineArguments) => dispatch(openTimeLine(timelineArguments)),
        })
    ),
    withAppClockService
);
